/**
 * Multi-Location Allocation Dialog
 *
 * Allows users to allocate quantities from multiple warehouse locations
 * for a single product in an outbound order
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import { Progress } from '../../ui/progress';
import {
  X,
  Sparkles,
  Trash2,
  MapPin
} from 'lucide-react';
import inventoryService from '../../../services/inventoryService';

// Helper to create unique key for location+client combination
const getLocationKey = (loc) => {
  const location = loc.location || 'main';
  const clientId = loc.source_client_id || loc.client_id || 'global';
  return `${location}__${clientId}`;
};

function MultiLocationAllocationDialog({ open, onClose, product, onConfirm }) {
  const [allocations, setAllocations] = useState({});
  const [error, setError] = useState(null);
  const [inventoryData, setInventoryData] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);

  // Fetch inventory data when dialog opens
  useEffect(() => {
    if (open && product) {
      const fetchInventory = async () => {
        setLoadingInventory(true);
        try {
          const productCode = product.product_code || product.product_id;
          const response = await inventoryService.getInventory(
            1, 1000, 'location', 'asc',
            { product_id: productCode },
            null // global view
          );
          setInventoryData(response.inventory || []);
        } catch (err) {
          console.error('Failed to fetch inventory:', err);
          setInventoryData([]);
        } finally {
          setLoadingInventory(false);
        }
      };
      fetchInventory();
    }
  }, [open, product]);

  // Initialize allocations when dialog opens or product changes
  useEffect(() => {
    if (open && product) {
      // If product already has allocations, use them
      if (product.locationAllocations && product.locationAllocations.length > 0) {
        const initialAllocations = {};
        product.locationAllocations.forEach(alloc => {
          // Use composite key: location__source_client_id
          const key = getLocationKey(alloc);
          initialAllocations[key] = alloc.quantity;
        });
        setAllocations(initialAllocations);
      } else {
        // Otherwise start fresh
        setAllocations({});
      }
      setError(null);
    }
  }, [open, product]);

  // Calculate totals
  const totals = useMemo(() => {
    if (!product) return { allocated: 0, required: 0, remaining: 0 };

    const allocated = Object.values(allocations).reduce((sum, qty) => sum + (qty || 0), 0);
    const required = product.quantity_planned || product.quantity_actual || 0;
    const remaining = required - allocated;

    return { allocated, required, remaining };
  }, [allocations, product]);

  // Filter and sort locations from inventory API
  const availableLocations = useMemo(() => {
    if (!inventoryData || inventoryData.length === 0) return [];

    const withStock = inventoryData.filter(loc =>
      (loc.available_quantity || 0) > 0
    );

    return withStock.sort((a, b) =>
      (b.available_quantity || 0) - (a.available_quantity || 0)
    );
  }, [inventoryData]);

  // Handle quantity change for a location (using composite key)
  const handleQuantityChange = (locationKey, locationData, value) => {
    const qty = parseFloat(value) || 0;

    if (!locationData) return;

    const availableQty = locationData.available_quantity || 0;

    if (qty > availableQty) {
      setError(`${locationData.location} 库存不足：仅有 ${availableQty}${product.unit || '支'}`);
      return;
    }

    setError(null);
    setAllocations(prev => ({
      ...prev,
      [locationKey]: qty
    }));
  };

  // Auto-allocate: Fill from locations with highest stock first
  const handleAutoAllocate = () => {
    const required = product.quantity_planned || product.quantity_actual || 0;
    const newAllocations = {};
    let remaining = required;

    for (const loc of availableLocations) {
      if (remaining <= 0) break;

      const availableQty = loc.available_quantity || 0;
      const allocate = Math.min(remaining, availableQty);
      if (allocate > 0) {
        const key = getLocationKey(loc);
        newAllocations[key] = allocate;
        remaining -= allocate;
      }
    }

    setAllocations(newAllocations);
    setError(null);
  };

  // Clear all allocations
  const handleClear = () => {
    setAllocations({});
    setError(null);
  };

  // Confirm allocation
  const handleConfirm = () => {
    // Validate total allocation
    if (totals.allocated === 0) {
      setError('请至少从一个位置分配数量');
      return;
    }

    if (totals.remaining > 0) {
      const frozenInfo = product.frozen_qty > 0 ? `\n\n注意：该产品已冻结 ${product.frozen_qty}${product.unit || '支'}，冻结部分无需手动分配。` : '';
      const confirmed = window.confirm(
        `还有 ${totals.remaining}${product.unit || '支'} 未分配。${frozenInfo}\n\n是否继续？`
      );
      if (!confirmed) return;
    }

    // Build allocation array using composite keys
    const locationAllocations = Object.entries(allocations)
      .filter(([_, qty]) => qty > 0)
      .map(([locationKey, quantity]) => {
        // Find location data by composite key
        const locationData = availableLocations.find(loc => getLocationKey(loc) === locationKey);
        return {
          location: locationData?.location || locationKey.split('__')[0],
          quantity,
          available_quantity: locationData?.available_quantity || 0,
          // Include source client info for cross-client transfer detection
          source_client_id: locationData?.source_client_id || locationData?.client_id,
          source_client_name: locationData?.client_name || locationData?.source_client_name,
          client_name: locationData?.client_name || locationData?.source_client_name,
          specification: locationData?.specification
        };
      });

    onConfirm({
      ...product,
      locationAllocations,
      totalAllocated: totals.allocated
    });
  };

  if (!product) return null;

  const progressPercentage = totals.required > 0 ? (totals.allocated / totals.required) * 100 : 0;
  const isOverAllocated = totals.allocated > totals.required;
  const isUnderAllocated = totals.allocated < totals.required;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex justify-between items-start">
            <div>
              <DialogTitle>多位置分配</DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {product.product_name} ({product.product_code})
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto pr-2">
          {/* Summary Card */}
        <Card className="p-4 mb-4 bg-muted">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-muted-foreground">
              需要数量
            </span>
            <Badge>
              {totals.required}{product.unit || '支'}
            </Badge>
          </div>

          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-muted-foreground">
              已分配
            </span>
            <Badge
              variant={isOverAllocated ? 'destructive' : totals.allocated === totals.required ? 'default' : 'secondary'}
              className={totals.allocated === totals.required ? 'bg-success/100' : ''}
            >
              {totals.allocated}{product.unit || '支'}
            </Badge>
          </div>

          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-muted-foreground">
              剩余
            </span>
            <span className="text-sm font-medium">
              {totals.remaining}{product.unit || '支'}
            </span>
          </div>

          <Progress
            value={Math.min(progressPercentage, 100)}
            className={`mt-2 h-2 ${
              isOverAllocated ? 'bg-destructive/20' : isUnderAllocated ? 'bg-warning/20' : 'bg-success/20'
            }`}
          />
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>×</Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Quick Actions */}
        <div className="flex gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleAutoAllocate}
          >
            <Sparkles className="h-4 w-4 mr-1" />
            智能分配
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClear}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            清空
          </Button>
        </div>

        {/* Location Allocation Table */}
        <Card className="border">
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">库位</th>
                  <th className="px-4 py-2 text-left font-semibold">规格</th>
                  <th className="px-4 py-2 text-left font-semibold">客户</th>
                  <th className="px-4 py-2 text-left font-semibold">可用库存</th>
                  <th className="px-4 py-2 text-left font-semibold w-[150px]">分配数量</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loadingInventory ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                      正在加载库存数据...
                    </td>
                  </tr>
                ) : availableLocations.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                      无可用库存
                    </td>
                  </tr>
                ) : (
                  availableLocations.map((loc) => {
      const availableQty = loc.available_quantity || 0;
                    const locationKey = getLocationKey(loc);
                    return (
                      <tr key={locationKey} className="hover:bg-muted">
                        {/* Location Name */}
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">
                              {loc.location || '-'}
                            </span>
                          </div>
                        </td>

                        {/* Specification */}
                        <td className="px-4 py-2">
                          <span className="text-muted-foreground">
                            {loc.specification || '-'}
                          </span>
                        </td>

                        {/* Client */}
                        <td className="px-4 py-2">
                          <span className="text-sm">
                            {loc.client_name || loc.source_client_name || '-'}
                          </span>
                        </td>

                        {/* Available Quantity */}
                        <td className="px-4 py-2">
                          <Badge variant="outline" className="border-green-500 text-success">
                            {availableQty}{product.unit || '支'}
                          </Badge>
                        </td>

                        {/* Allocation Input */}
                        <td className="px-4 py-2">
                          <Input
                            type="number"
                            value={allocations[locationKey] || ''}
                            onChange={(e) => handleQuantityChange(locationKey, loc, e.target.value)}
                            placeholder="0"
                            min={0}
                            max={availableQty}
                            step={1}
                            className={`w-full ${
                              allocations[locationKey] > availableQty
                                ? 'border-red-500'
                                : ''
                            }`}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Info Messages */}
        {isOverAllocated && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>
              分配数量超出需要数量 {totals.allocated - totals.required}{product.unit || '支'}
            </AlertDescription>
          </Alert>
        )}

        {isUnderAllocated && totals.allocated > 0 && (
          <Alert variant="warning" className="mt-4 border-yellow-500 bg-warning/10">
            <AlertDescription>
              还有 {totals.remaining}{product.unit || '支'} 未分配
            </AlertDescription>
          </Alert>
        )}
        </div>

        <DialogFooter className="mt-4 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button
            onClick={handleConfirm}
            disabled={totals.allocated === 0 || isOverAllocated}
          >
            确认分配
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MultiLocationAllocationDialog;
